// src/components/tactic/TeamScoutDialog.jsx
// ─────────────────────────────────────────────────────────────────────────
// The scouting window: pick a team, read our heroes on top and theirs below,
// then click any hero to see who it met, how that went, and what it gets
// played with.
//
// It is a full-screen sheet rather than a rail panel because that is how it
// gets used — a coach opens it while drafting and wants the whole table at
// once, then closes it with the × and goes back to the board.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";
import heroList from "../../data/heroList.json";
import { getHeroImageUrlWithFallback } from "../../utils/heroCatalog";
import { isFsWin } from "../../utils/heroMatchups";
import { TEAM_SHORT } from "../../theme/brandTheme";
import {
  ALL_TEAMS_KEY,
  BOARD_LANES,
  ROLE_LABELS,
  heroPoolRows,
  compsForHero,
  gamesVsTeam,
  heroesOnSide,
  matchupsForHero,
  rankByWinRate,
  recordVsTeam,
  teammatesForHero,
  teamsInLog,
} from "../../utils/tactic/teamScout";
import { Btn, Label, Row, T, TextInput } from "./tacticUi";

const CUTS = [1, 2, 3, 5];
// Heroes shown per lane row before the row offers the rest.
const LANE_CAP = 12;

const CATALOG = Array.isArray(heroList) ? heroList : heroList?.heroes || [];
const CATALOG_COUNT = new Set(CATALOG.map((h) => h?.name).filter(Boolean)).size;

const rateColor = (wr) => (wr >= 50 ? T.green : T.red);

function Portrait({ name, role, size = 34 }) {
  return (
    <img
      src={getHeroImageUrlWithFallback(name, role)}
      alt={name}
      title={name}
      loading="lazy"
      style={{
        width: size,
        height: size,
        borderRadius: 7,
        objectFit: "cover",
        flexShrink: 0,
        background: "#0b0d12",
      }}
    />
  );
}

/** games · W-L · WR%, the three numbers every row here ends with. */
function Record({ row, compact }) {
  return (
    <>
      <span style={{ color: T.accent, fontWeight: 600, fontSize: compact ? 11.5 : 12.5 }}>
        {row.games} เกม
      </span>
      <span style={{ fontSize: compact ? 11.5 : 12.5, whiteSpace: "nowrap" }}>
        <b style={{ color: T.green }}>{row.wins}</b>
        <span style={{ color: T.faint }}>-</span>
        <b style={{ color: T.red }}>{row.losses}</b>
      </span>
      <span
        style={{
          minWidth: 46,
          textAlign: "right",
          fontWeight: 700,
          fontSize: compact ? 11.5 : 12.5,
          color: rateColor(row.winRate),
        }}
      >
        {row.winRate}%
      </span>
    </>
  );
}

function HeroTile({ row, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${row.name} — กดดูว่าเจอตัวไหนบ่อย`}
      style={{
        appearance: "none",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 9px",
        borderRadius: 9,
        background: "rgba(255,255,255,0.035)",
        border: `1px solid ${T.line}`,
        color: T.ink,
        fontFamily: T.font,
        cursor: "pointer",
        minWidth: 0,
        opacity: row.games ? 1 : 0.55,
      }}
    >
      <Portrait name={row.name} role={row.role} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.name}
        </span>
        {/* The lane is the row's job to say, so the tile only carries games. */}
        <span style={{ fontSize: 10.5, color: T.faint }}>
          {row.games ? `${row.games} เกม` : "ยังไม่เคยเล่น"}
          {row.watched > 0 && ` · ส่อง ${row.watched}`}
        </span>
      </span>
      <span
        style={{
          fontWeight: 700,
          fontSize: 12.5,
          color: row.games ? rateColor(row.winRate) : T.faint,
          flexShrink: 0,
        }}
      >
        {row.games ? `${row.winRate}%` : "—"}
      </span>
    </button>
  );
}

/** One lane of one side — the coach's board reads a row per position. */
function LaneRow({ label, board, rows, onPick, name, cap = LANE_CAP }) {
  const [all, setAll] = useState(false);
  const shown = all || !Number.isFinite(cap) ? rows : rows.slice(0, cap);

  return (
    <div role="group" aria-label={name} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
      <div
        style={{
          flexShrink: 0,
          width: 62,
          paddingTop: 6,
          fontSize: 11.5,
          fontWeight: 600,
          color: T.dim,
          letterSpacing: ".02em",
        }}
      >
        {label}
        {board && (
          <div style={{ fontSize: 9.5, color: T.faint, fontWeight: 400, letterSpacing: ".04em" }}>
            {board}
          </div>
        )}
        <div style={{ fontSize: 10, color: T.faint, fontWeight: 400 }}>{rows.length} ตัว</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(186px, 1fr))",
            gap: 6,
          }}
        >
          {shown.map((row) => (
            <HeroTile key={row.name} row={row} onClick={() => onPick(row)} />
          ))}
        </div>
        {Number.isFinite(cap) && rows.length > cap && (
          <Btn
            tone="ghost"
            onClick={() => setAll((v) => !v)}
            style={{ marginTop: 6, padding: "3px 8px", fontSize: 11.5 }}
          >
            {all ? "ย่อ" : `อีก ${rows.length - cap} ตัว`}
          </Btn>
        )}
      </div>
    </div>
  );
}

/** One side of the team view: a heading, the record, then a row per position. */
function HeroSection({ title, tone, record, groups, count, onPick, emptyText, cap, note }) {
  const lanes = groups.filter((lane) => lane.rows.length);

  return (
    <section aria-label={title} style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 9,
          paddingBottom: 7,
          borderBottom: `1px solid ${tone}44`,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: tone }}>{title}</h3>
        {note && (
          <span
            style={{
              fontSize: 10.5,
              color: tone,
              border: `1px solid ${tone}55`,
              background: `${tone}14`,
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            {note}
          </span>
        )}
        {record?.games > 0 && (
          <span style={{ fontSize: 12, color: T.dim }}>
            {record.games} เกม ·{" "}
            <b style={{ color: T.green }}>{record.wins}</b>
            <span style={{ color: T.faint }}>-</span>
            <b style={{ color: T.red }}>{record.losses}</b>{" "}
            ({record.winRate}%)
            {record.watched > 0 && (
              <span style={{ color: T.faint }}>
                {" "}
                (กับเรา {record.vsUs} · ส่องมา {record.watched})
              </span>
            )}
          </span>
        )}
        <span style={{ fontSize: 11.5, color: T.faint, marginLeft: "auto" }}>
          {count} ตัว
        </span>
      </div>

      {lanes.length === 0 ? (
        <div style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.7 }}>{emptyText}</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {lanes.map((lane) => (
            <LaneRow
              key={lane.key}
              label={lane.label}
              board={lane.board}
              rows={lane.rows}
              onPick={onPick}
              name={`${title} · ${lane.label}`}
              cap={cap}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** A hero row inside the matchup tables, with a bar for how often it was met. */
function MatchupRow({ row, topGames }) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "6px 9px",
        borderRadius: 8,
        border: `1px solid ${T.line}`,
        background: "rgba(255,255,255,0.03)",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${topGames ? (row.games / topGames) * 100 : 0}%`,
          background: "rgba(255,16,38,0.13)",
        }}
      />
      <Portrait name={row.name} role={row.role} size={28} />
      <span
        style={{
          zIndex: 1,
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.name}
        {row.role && (
          <span style={{ color: T.faint, fontSize: 10.5, marginLeft: 5 }}>
            {ROLE_LABELS[row.role] || row.role}
          </span>
        )}
      </span>
      <Record row={row} compact />
    </div>
  );
}

function CompRow({ row }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "6px 9px",
        borderRadius: 8,
        border: `1px solid ${T.line}`,
        background: "rgba(255,255,255,0.03)",
      }}
    >
      <span style={{ display: "flex", gap: 3, flexShrink: 0 }}>
        {row.heroes.map((hero, i) => (
          <Portrait key={`${hero}-${i}`} name={hero} role={row.roles[i]} size={28} />
        ))}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.name}
        </span>
        <span style={{ fontSize: 10.5, color: T.faint }}>
          {row.roles.map((r) => ROLE_LABELS[r] || r).join(" + ")}
        </span>
      </span>
      <Record row={row} compact />
    </div>
  );
}

/** Top-5 / bottom-5 by win rate, side by side. */
function RankColumn({ title, tone, rows, note, considered, subtitle }) {
  return (
    <section aria-label={title} style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: tone, marginBottom: 7 }}>
        {title}
        {subtitle && (
          <span
            style={{
              fontSize: 10,
              color: tone,
              border: `1px solid ${tone}55`,
              borderRadius: 999,
              padding: "1px 7px",
              marginLeft: 6,
              fontWeight: 500,
            }}
          >
            {subtitle}
          </span>
        )}
        {/* How big the pool was — with only a few pairs above the cut, the two
            columns overlap, and this is what says why. */}
        {considered > 0 && (
          <span style={{ color: T.faint, fontWeight: 400, marginLeft: 6 }}>
            จาก {considered} คู่
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.7 }}>{note}</div>
      ) : (
        <div style={{ display: "grid", gap: 5 }}>
          {rows.map((row) => (
            <MatchupRow key={row.name} row={row} topGames={0} />
          ))}
        </div>
      )}
    </section>
  );
}

function HeroDetail({ games, hero, onBack, onSideChange, usGames }) {
  const [enemyRole, setEnemyRole] = useState("");
  const [minGames, setMinGames] = useState(2);
  const [compSize, setCompSize] = useState(2);
  const [allRows, setAllRows] = useState(false);
  const [allComps, setAllComps] = useState(false);

  const side = hero.side;
  // A form tile ("Flowborn Carry") pins the hero to the lane that form plays,
  // so its numbers are that form's and not both shapes added together.
  const heroRole = hero.pinLane || "";
  const matchup = useMemo(
    () => matchupsForHero(games, { hero: hero.name, side, heroRole, enemyRole }),
    [games, hero.name, side, heroRole, enemyRole]
  );
  // How many of those games we played against them, so a merged read never
  // hides which sheet it leant on.
  const fromUs = useMemo(
    () => (usGames ? matchupsForHero(usGames, { hero: hero.name, side, heroRole }) : null),
    [usGames, hero.name, side, heroRole]
  );
  const watched = fromUs ? matchup.totalGames - fromUs.totalGames : 0;
  // Two different questions, one on each side: who to pair this hero with, and
  // who beats it. The old pair of columns ranked the same list twice, which on a
  // small pool printed the same heroes left and right.
  const mates = useMemo(
    () => teammatesForHero(games, { hero: hero.name, side, heroRole }),
    [games, hero.name, side, heroRole]
  );
  const bestMates = useMemo(
    () => rankByWinRate(mates.rows, { minGames, count: 5 }),
    [mates.rows, minGames]
  );
  const ranked = useMemo(() => rankByWinRate(matchup.rows, { minGames, count: 5 }), [matchup.rows, minGames]);
  const comps = useMemo(
    () => compsForHero(games, { hero: hero.name, side, size: compSize, heroRole }),
    [games, hero.name, side, compSize, heroRole]
  );

  const topGames = matchup.rows[0]?.games || 0;
  const shownRows = allRows ? matchup.rows : matchup.rows.slice(0, 12);
  const shownComps = allComps ? comps : comps.slice(0, 12);

  return (
    <>
      <Row gap={11} style={{ marginBottom: 14 }}>
        <Btn tone="ghost" onClick={onBack}>← กลับไปรายชื่อ</Btn>
        {/* The same hero on the other bench is one click away — a coach asks
            both questions about it, ours and theirs. */}
        <Btn
          tone="ghost"
          active={side === "fs"}
          onClick={() => onSideChange("fs")}
          style={{ padding: "5px 9px", fontSize: 12 }}
        >
          ฮีโร่ของเรา
        </Btn>
        <Btn
          tone="ghost"
          active={side === "op"}
          onClick={() => onSideChange("op")}
          style={{ padding: "5px 9px", fontSize: 12 }}
        >
          ฮีโร่คู่แข่ง
        </Btn>
      </Row>

      <Row gap={12} style={{ marginBottom: 16 }}>
        <Portrait name={hero.name} role={hero.role} size={62} />
        <span>
          <div style={{ fontSize: 21, fontWeight: 600 }}>
            {hero.name}
            {hero.role && (
              <span style={{ fontSize: 13, color: T.dim, marginLeft: 8 }}>
                {ROLE_LABELS[hero.role] || hero.role}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: T.dim, marginTop: 3 }}>
            {matchup.totalGames} เกม ·{" "}
            <b style={{ color: T.green }}>{matchup.wins}</b>
            <span style={{ color: T.faint }}>-</span>
            <b style={{ color: T.red }}>{matchup.losses}</b>{" "}
            <b style={{ color: rateColor(matchup.winRate) }}>({matchup.winRate}%)</b>
            {watched > 0 && (
              <span style={{ color: T.faint }}>
                {" "}
                (กับเรา {fromUs.totalGames} · ส่องมา {watched})
              </span>
            )}
          </div>
        </span>
      </Row>

      <Label>ดูเฉพาะตำแหน่งของตัวที่เจอ</Label>
      <Row gap={5} style={{ marginBottom: 10 }}>
        <Btn tone="ghost" active={!enemyRole} onClick={() => setEnemyRole("")} style={{ padding: "5px 9px", fontSize: 12 }}>
          ทุกตำแหน่ง
        </Btn>
        {BOARD_LANES.map(({ role }) => (
          <Btn
            key={role}
            tone="ghost"
            active={enemyRole === role}
            onClick={() => setEnemyRole(role)}
            style={{ padding: "5px 9px", fontSize: 12 }}
          >
            {ROLE_LABELS[role]}
          </Btn>
        ))}
      </Row>

      <Label>จัดอันดับจากที่เล่นด้วย/เจอกันตั้งแต่</Label>
      <Row gap={5} style={{ marginBottom: 14 }}>
        {CUTS.map((n) => (
          <Btn
            key={n}
            tone="ghost"
            active={minGames === n}
            onClick={() => setMinGames(n)}
            style={{ padding: "5px 9px", fontSize: 12 }}
          >
            {n === 1 ? "ทุกคู่" : `${n} เกมขึ้นไป`}
          </Btn>
        ))}
      </Row>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(268px, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <RankColumn
          title="5 ตัวที่เล่นด้วยดีที่สุด"
          subtitle="ฝั่งเดียวกัน"
          tone={T.green}
          rows={bestMates.best}
          considered={bestMates.considered}
          note={`ยังไม่มีตัวที่เล่นด้วยถึง ${minGames} เกม — กด "ทุกคู่" เพื่อดูทั้งหมด`}
        />
        <RankColumn
          title="5 ตัวที่เจอแล้วแย่ที่สุด"
          subtitle="ฝั่งตรงข้าม"
          tone={T.red}
          rows={ranked.worst}
          considered={ranked.considered}
          note={`ยังไม่มีคู่ที่เจอกันถึง ${minGames} เกม — กด "ทุกคู่" เพื่อดูทั้งหมด`}
        />
      </div>

      <section aria-label="ตารางเจอตัวไหนบ่อย" style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          เจอตัวไหนบ่อย{" "}
          <span style={{ color: T.faint, fontWeight: 400 }}>({matchup.rows.length} ตัว)</span>
        </div>
        {matchup.rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.7 }}>
            ไม่มีเกมของฮีโร่ตัวนี้ในทีมที่เลือก
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 5 }}>
              {shownRows.map((row) => (
                <MatchupRow key={row.name} row={row} topGames={topGames} />
              ))}
            </div>
            {matchup.rows.length > 12 && (
              <Btn tone="ghost" onClick={() => setAllRows((v) => !v)} style={{ marginTop: 8 }}>
                {allRows ? "ย่อรายการ" : `ดูทั้งหมด (${matchup.rows.length})`}
              </Btn>
            )}
          </>
        )}
      </section>

      <section aria-label="คอมที่เล่นด้วย">
        <Row gap={8} style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>คอมที่ชอบเล่นกับ {hero.name}</span>
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 5 }}>
            <Btn
              tone="ghost"
              active={compSize === 2}
              onClick={() => { setCompSize(2); setAllComps(false); }}
              style={{ padding: "5px 9px", fontSize: 12 }}
            >
              คอม 2 ตัว
            </Btn>
            <Btn
              tone="ghost"
              active={compSize === 3}
              onClick={() => { setCompSize(3); setAllComps(false); }}
              style={{ padding: "5px 9px", fontSize: 12 }}
            >
              คอม 3 ตัว
            </Btn>
          </span>
        </Row>
        {comps.length === 0 ? (
          <div style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.7 }}>
            ยังไม่มีคอมของฮีโร่ตัวนี้ในทีมที่เลือก
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 5 }}>
              {shownComps.map((row) => (
                <CompRow key={row.name} row={row} />
              ))}
            </div>
            {comps.length > 12 && (
              <Btn tone="ghost" onClick={() => setAllComps((v) => !v)} style={{ marginTop: 8 }}>
                {allComps ? "ย่อรายการ" : `ดูทั้งหมด (${comps.length})`}
              </Btn>
            )}
          </>
        )}
      </section>
    </>
  );
}

export default function TeamScoutDialog({
  games,
  team,
  onTeamChange,
  onClose,
  scope,
  onScopeChange,
  scopeCounts,
  scopeLoading = false,
  initialPool = "played",
  initialHero = "",
  initialLane = "",
  // Specter_Scrim: the selected team watched playing everyone else, already in
  // game shape with them on the "op" side.
  scouted = [],
  scoutedTotal = 0,
  // "us" = only games against our team, "scouted" = only Specter_Scrim, "both" = merged
  source = "both",
  onSourceChange,
  // Teams to offer: the page merges our log with the scouting sheet, so a team
  // we have never faced can still be picked.
  teams: teamsProp,
}) {
  const [hero, setHero] = useState(null);
  const [query, setQuery] = useState("");
  // "played" = the lanes this side actually stood in; "all" = the whole hero
  // pool in the board's six class rows, never-played heroes included.
  const [poolMode, setPoolMode] = useState(initialPool);

  const teams = useMemo(
    () => (teamsProp?.length ? teamsProp : teamsInLog(games)),
    [teamsProp, games]
  );
  const pool = useMemo(() => gamesVsTeam(games, team), [games, team]);
  // Their side can be read from either sheet or both; ours can only ever be
  // games FS played.
  const theirPool = useMemo(() => {
    if (source === "us") return pool;
    if (source === "scouted") return scouted;
    return scouted.length ? [...pool, ...scouted] : pool;
  }, [source, pool, scouted]);

  const ours = useMemo(() => heroesOnSide(pool, "fs"), [pool]);
  const theirs = useMemo(() => {
    const rows = heroesOnSide(theirPool, "op");
    if (source !== "both" || !scouted.length) return rows;
    // Merged view: every tile still says how much of it we watched rather than
    // played, so the two sheets never blur into one number.
    const vsUs = new Map(heroesOnSide(pool, "op").map((r) => [r.name, r.games]));
    return rows.map((r) => {
      const mine = vsUs.get(r.name) || 0;
      return { ...r, vsUs: mine, watched: r.games - mine };
    });
  }, [theirPool, source, scouted, pool]);

  // Our head-to-head record over the pool on screen; "ทุกทีม" sums the log.
  const record = useMemo(() => recordVsTeam(games, team), [games, team]);

  // Their record: our games against them (their wins are our losses) plus the
  // watched ones, where they won whatever the log's FS flag says they did not.
  const theirRecord = useMemo(() => {
    const fromUs = source === "scouted" ? { games: 0, wins: 0 } : { games: record.games, wins: record.losses };
    let games = fromUs.games;
    let wins = fromUs.wins;
    if (source !== "us") {
      for (const g of scouted) {
        games += 1;
        if (!isFsWin(g)) wins += 1;
      }
    }
    return games
      ? {
          games,
          wins,
          losses: games - wins,
          winRate: Number(((wins / games) * 100).toFixed(1)),
          // The split only means something when both sheets are on screen.
          vsUs: fromUs.games,
          watched: source === "both" ? scouted.length : 0,
        }
      : null;
  }, [record, scouted, source]);

  // A hero row for one side, falling back to an empty record for a hero that
  // side never played — the detail view then says so instead of rendering blank.
  const rowFor = (name, side, pinLane = "") => {
    const rows = side === "op" ? theirs : ours;
    const found = rows.find((r) => r.name === name);
    return {
      ...(found || { name, role: "", games: 0, wins: 0, losses: 0, winRate: 0 }),
      side,
      pinLane,
    };
  };

  // Tiles in the pool view may stand for one form of a hero the log keys under
  // a single name — open that name, pinned to the form's lane.
  const pickRow = (row, side) =>
    row.logName
      ? { ...rowFor(row.logName, side, row.role), pinLane: row.role }
      : { ...row, side, pinLane: "" };

  // Switching team invalidates whichever hero was open — its numbers were for
  // the old pool.
  useEffect(() => { setHero(null); }, [team]);

  // Opened from a hero tile in the shelf: go straight to that hero. Declared
  // after the reset above so it wins on the mount they share.
  useEffect(() => {
    if (initialHero) setHero(rowFor(initialHero, "fs", initialLane));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHero, initialLane]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      if (hero) setHero(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [hero, onClose]);

  const q = query.trim().toLowerCase();
  const teamName = team === ALL_TEAMS_KEY ? "ทุกทีม" : team;

  /**
   * Rows for one side, grouped the way the board is drawn, then narrowed by the
   * search box. Grouping happens before filtering so a search never moves a
   * hero out of its row.
   */
  const groupsFor = (played) => {
    const groups =
      poolMode === "all"
        ? heroPoolRows(CATALOG, played)
        : BOARD_LANES.map(({ role, board }) => ({
            key: role,
            label: ROLE_LABELS[role] || role,
            board,
            rows: played.filter((r) => r.role === role),
          }));
    if (!q) return groups;
    return groups.map((g) => ({ ...g, rows: g.rows.filter((r) => r.name.toLowerCase().includes(q)) }));
  };

  // Heroes on screen for a side — counted once even when a dual-class hero
  // shows up in two of the board's rows.
  const countIn = (groups) => new Set(groups.flatMap((g) => g.rows.map((r) => r.name))).size;

  const ourGroups = groupsFor(ours);
  const theirGroups = groupsFor(theirs);
  // The board shows the whole row at once, so the pool view does too.
  const rowCap = poolMode === "all" ? Infinity : LANE_CAP;

  return (
    <div
      onPointerDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,6,10,0.82)",
        display: "grid",
        placeItems: "center",
        zIndex: 90,
        padding: "2vh 2vw",
      }}
    >
      <div
        role="dialog"
        aria-label="สถิติทีม"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          background: T.panelSolid,
          border: `1px solid ${T.line}`,
          borderRadius: 14,
          boxShadow: "0 30px 90px rgba(0,0,0,0.7)",
          // A wide screen should get a wide sheet: the pool view is six rows of
          // tiles, and every extra 190px is another hero per row.
          width: "min(1680px, 96vw)",
          maxHeight: "96vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header — the close button sits top right, where it was asked for. */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "13px 16px",
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            สถิติทีม
            <span style={{ color: T.faint, fontSize: 12.5, fontWeight: 400, marginLeft: 9 }}>
              กดที่ฮีโร่เพื่อดูว่าเจอตัวไหนบ่อย · วินเรท · คอมที่เล่นด้วย
            </span>
          </span>

          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, marginLeft: "auto" }}>
            <span style={{ fontSize: 11.5, color: T.faint }}>ทีม</span>
            <select
              value={team}
              onChange={(e) => onTeamChange(e.target.value)}
              aria-label="เลือกทีม"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: `1px solid ${T.line}`,
                borderRadius: 8,
                color: T.ink,
                fontFamily: T.font,
                fontSize: 13,
                padding: "7px 9px",
                maxWidth: 240,
              }}
            >
              <option value={ALL_TEAMS_KEY}>ทุกทีม</option>
              {teams.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.code} (
                  {[t.games ? `${t.games} เกม` : "", t.scouted ? `ส่อง ${t.scouted}` : ""]
                    .filter(Boolean)
                    .join(" · ")}
                  )
                </option>
              ))}
            </select>
            <TextInput
              placeholder="ค้นหาฮีโร่…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 168 }}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="ปิด"
              title="ปิด (Esc)"
              style={{
                appearance: "none",
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${T.line}`,
                background: "rgba(255,255,255,0.05)",
                color: T.ink,
                fontSize: 18,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </span>
        </header>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            padding: "9px 16px",
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          {typeof onScopeChange === "function" && (
            <>
              <span style={{ fontSize: 11.5, color: T.faint }}>ช่วงข้อมูล</span>
              <Btn
                tone="ghost"
                active={scope === "season"}
                onClick={() => onScopeChange("season")}
                title="log ทุกเกมของซีซั่นจากชีท All_Scrim"
                style={{ padding: "5px 9px", fontSize: 12 }}
              >
                {scopeLoading
                  ? "กำลังโหลด…"
                  : `ทั้งซีซั่น${scopeCounts?.season ? ` (${scopeCounts.season})` : ""}`}
              </Btn>
              <Btn
                tone="ghost"
                active={scope === "current"}
                onClick={() => onScopeChange("current")}
                title="เฉพาะเกมในชีทสรุปที่โหลดอยู่"
                style={{ padding: "5px 9px", fontSize: 12 }}
              >
                ชีทนี้{scopeCounts?.current ? ` (${scopeCounts.current})` : ""}
              </Btn>
              <span style={{ fontSize: 11, color: T.faint, marginLeft: 4 }}>
                {source === "scouted"
                  ? `${scouted.length.toLocaleString()} เกมที่ส่องมา`
                  : `${pool.length.toLocaleString()} เกม${
                      team === ALL_TEAMS_KEY ? "ในกองนี้" : ` กับ ${team}`
                    }`}
              </span>
            </>
          )}

          {typeof onSourceChange === "function" && scoutedTotal > 0 && (
            <>
              <span style={{ fontSize: 11.5, color: T.faint, marginLeft: 6 }}>
                ข้อมูลคู่แข่ง
              </span>
              {[
                { key: "us", label: `เกมกับเรา (${record.games})`, hint: `เฉพาะเกมที่ทีมนั้นเจอกับ ${TEAM_SHORT} — จาก log ของเรา` },
                { key: "scouted", label: `ที่ส่องมา (${scouted.length})`, hint: "เฉพาะชีท Specter_Scrim — เกมของทีมนั้นกับทีมอื่น" },
                { key: "both", label: "รวมสองชีท", hint: "รวมทั้งเกมที่เจอเราและที่ส่องมา" },
              ].map((opt) => (
                <Btn
                  key={opt.key}
                  tone="ghost"
                  active={source === opt.key}
                  onClick={() => onSourceChange(opt.key)}
                  title={opt.hint}
                  style={{ padding: "5px 9px", fontSize: 12 }}
                >
                  {opt.label}
                </Btn>
              ))}
            </>
          )}

          {/* Which heroes the rows hold — only the team's picks, or the whole
              pool laid out like the board on the wall. */}
          {!hero && (
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11.5, color: T.faint }}>แสดง</span>
              <Btn
                tone="ghost"
                active={poolMode === "played"}
                onClick={() => setPoolMode("played")}
                title="เฉพาะฮีโร่ที่ทีมนั้นเคยเล่นจริงใน log"
                style={{ padding: "5px 9px", fontSize: 12 }}
              >
                เฉพาะที่เคยเล่น
              </Btn>
              <Btn
                tone="ghost"
                active={poolMode === "all"}
                onClick={() => setPoolMode("all")}
                title="ฮีโร่ทั้งหมดในเกม เรียงตามตารางดราฟ (CARRY / JUNGLE / MAGE / FIGHTER / TANK / SUPPORT)"
                style={{ padding: "5px 9px", fontSize: 12 }}
              >
                ฮีโร่ทั้งหมด ({CATALOG_COUNT})
              </Btn>
            </span>
          )}
        </div>

        <div style={{ padding: 16, overflowY: "auto" }}>
          {!pool.length ? (
            <div style={{ color: T.faint, fontSize: 13, lineHeight: 1.8 }}>
              ยังไม่มีข้อมูลเกมของทีมนี้ — อัปโหลด .xlsx ที่หน้าแรก แล้วสถิติจาก log ทุกเกม
              จะมาโผล่ที่นี่
            </div>
          ) : hero ? (
            <HeroDetail
              games={hero.side === "op" ? theirPool : pool}
              usGames={hero.side === "op" && source === "both" && scouted.length ? pool : null}
              hero={hero}
              onBack={() => setHero(null)}
              onSideChange={(side) => setHero(rowFor(hero.name, side, hero.pinLane))}
            />
          ) : (
            <>
              {/* The scouted sheet has no game of ours in it, so our half is only
                  ever noise there. */}
              {source !== "scouted" && (
                <HeroSection
                  title={`ของเรา — ${TEAM_SHORT}`}
                  tone={T.blue}
                  record={record}
                  groups={ourGroups}
                  count={countIn(ourGroups)}
                  cap={rowCap}
                  onPick={(row) => setHero(pickRow(row, "fs"))}
                  emptyText="ไม่พบฮีโร่ที่ค้นหาในฝั่งเรา"
                />
              )}
              <HeroSection
                title={`คู่แข่ง — ${teamName}`}
                note={
                  scoutedTotal > 0
                    ? source === "us"
                      ? "เฉพาะเกมที่เจอเรา"
                      : source === "scouted"
                      ? "เฉพาะที่ส่องมา (Specter_Scrim)"
                      : "รวมสองชีท"
                    : ""
                }
                tone={T.red}
                record={theirRecord}
                groups={theirGroups}
                count={countIn(theirGroups)}
                cap={rowCap}
                onPick={(row) => setHero(pickRow(row, "op"))}
                emptyText={
                  source === "scouted"
                    ? "ยังไม่มีเกมของทีมนี้ในชีท Specter_Scrim"
                    : "ไม่พบฮีโร่ที่ค้นหาในฝั่งคู่แข่ง"
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
